import SendInviteIcon from '@mui/icons-material/Email';
import { Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useCallback, useMemo, useState } from 'react';
import { Confirm, useListContext, useNotify } from 'react-admin';

import { PostSendEmployeeActivatedInvitationBulkEndpoint } from '@Common/types/b2b/invitations';
import { GetB2bEmployeesOneResponse } from '@Common/types/bo/b2b/employees';

import { Button } from '@sh4res/bo-common/components';
import { useCreatePromise, useResourceBasePath, useValueChanged } from '@sh4res/bo-common/hooks';

const useStyles = makeStyles((theme) => ({
  bulkInvitesButton: {
    marginTop: theme.spacing(0.5),
  },
}));

export const BulkSendInvitesButton = ({ testId }: { testId: string }) => {
  const { selectedIds, onUnselectItems, filterValues, refetch, data, page } =
    useListContext<GetB2bEmployeesOneResponse>();
  const [bulkSendInvites, { isLoading: bulkSendInvitesLoading }] = useCreatePromise<
    PostSendEmployeeActivatedInvitationBulkEndpoint['Client']['RequestBody'],
    PostSendEmployeeActivatedInvitationBulkEndpoint['Client']['ResponseBody']
  >();
  const notify = useNotify();
  const styles = useStyles();
  const resourceBasePath = useResourceBasePath();

  useValueChanged(filterValues, onUnselectItems);
  useValueChanged(page, onUnselectItems);

  const [confirmationOpen, setConfirmationOpen] = useState<boolean>(false);

  const openConfirmationModal = useCallback(() => setConfirmationOpen(true), [setConfirmationOpen]);

  const closeConfirmationOpen = useCallback(
    () => setConfirmationOpen(false),
    [setConfirmationOpen],
  );

  const dataMap = useMemo(() => new Map(data.map((item) => [item.id, item])), [data]);

  const disabled = useMemo(
    () => selectedIds.some((id) => dataMap.get(id)?.kyb_status !== 'verified'),
    [dataMap, selectedIds],
  );

  const handleSendBulkInvite = useCallback(async () => {
    try {
      await bulkSendInvites(`${resourceBasePath}employees/bulk_activated_invitation`, {
        data: {
          employee_ids: selectedIds,
        },
      });
      closeConfirmationOpen();
      onUnselectItems();
      refetch();
      notify('resources.bo_common.b2b.employees.actions.bulk_invite.success', {
        type: 'success',
      });
    } catch (error) {
      notify('resources.bo_common.b2b.employees.actions.bulk_invite.failure', {
        type: 'error',
        messageArgs: { error },
        multiLine: true,
      });
      closeConfirmationOpen();
    }
  }, [
    selectedIds,
    onUnselectItems,
    refetch,
    bulkSendInvites,
    notify,
    closeConfirmationOpen,
    resourceBasePath,
  ]);

  return (
    <Grid container>
      <Button
        label="resources.bo_common.b2b.employees.actions.bulk_invite.label"
        onClick={openConfirmationModal}
        className={styles.bulkInvitesButton}
        disabled={bulkSendInvitesLoading || disabled}
        data-testid={testId}
        permissions={{
          resource: `${resourceBasePath}employees/bulk_activated_invitation`,
          action: 'create',
        }}>
        <SendInviteIcon />
      </Button>
      <Confirm
        isOpen={confirmationOpen}
        title="Confirmation"
        content="resources.bo_common.b2b.employees.actions.bulk_invite.confirm"
        onConfirm={handleSendBulkInvite}
        onClose={closeConfirmationOpen}
      />
    </Grid>
  );
};
